import React, { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import moment from "moment"
import { FaGraduationCap, FaChevronDown, FaChevronUp } from "react-icons/fa"
import { getLearnWorldsEnrolments } from "../../../requesters/Backend/UserRequester"
import Pagination from "../../Pagination"

const ITEMS_PER_PAGE = 10

// LearnWorlds nests the course under `course` and returns Unix timestamps
// (seconds). Read defensively so a missing key degrades to a dash.
const courseName = enrolment => enrolment.course?.title || enrolment.course?.id || "—"

const courseAuthor = enrolment => enrolment.course?.author?.name || "—"

// Renders a Unix timestamp (seconds) as a date, or a fallback when absent.
const formatUnix = (value, fallback = "—") => {
  if (!value) return fallback
  const m = typeof value === "number" ? moment.unix(value) : moment(value)
  return m.isValid() ? m.format("DD MMM YYYY") : fallback
}

const totalFromMeta = (meta, fallback) => meta?.totalItems ?? meta?.total_items ?? meta?.total ?? fallback

function LearnWorldsCourses({ baseUrl, currentUser, learnWorldUserId }) {
  const [expanded, setExpanded] = useState(false)
  const [enrolments, setEnrolments] = useState([])
  const [total, setTotal] = useState(0)
  const [perPage, setPerPage] = useState(ITEMS_PER_PAGE)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const fetchEnrolments = useCallback(() => {
    setIsLoading(true)
    const params = { learn_world_user_id: learnWorldUserId, page, items_per_page: ITEMS_PER_PAGE }
    getLearnWorldsEnrolments(baseUrl, params, currentUser)
      .then(res => {
        const list = res?.response?.enrolments || []
        const meta = res?.response?.meta
        setEnrolments(list)
        setTotal(totalFromMeta(meta, list.length))
        // LearnWorlds drives its own page size, so size the pager from what it
        // actually returned rather than what we requested.
        setPerPage(meta?.itemsPerPage || meta?.items_per_page || ITEMS_PER_PAGE)
        setHasFetched(true)
        setIsLoading(false)
        return null
      })
      .catch(() => {
        setIsLoading(false)
        toast.error("Could not load LearnWorlds courses")
      })
  }, [baseUrl, currentUser, learnWorldUserId, page])

  // Lazy load: only hit the LearnWorlds API once the section is expanded, and
  // re-fetch when the admin pages through results. The main page never triggers
  // this request on load.
  useEffect(() => {
    if (expanded) fetchEnrolments()
  }, [expanded, fetchEnrolments])

  const handleToggle = () => {
    if (expanded) setPage(1)
    setExpanded(!expanded)
  }

  if (!learnWorldUserId) {
    return (
      <div className="card">
        <div className="card-header">
          <h5>
            <FaGraduationCap />
            LearnWorlds Courses
          </h5>
        </div>
        <div className="card-body">
          <p className="mb-0">This user has no linked LearnWorlds account.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <FaGraduationCap />
          LearnWorlds Courses
        </h5>
        <button className="btn btn-outline-main btn-icon" onClick={handleToggle} type="button">
          {expanded ? <FaChevronUp /> : <FaChevronDown />}
          {expanded ? "Hide" : "Show Courses"}
        </button>
      </div>
      {expanded && (
        <div className="card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Author</th>
                    <th>Enrolled</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {enrolments.length !== 0 ? (
                    enrolments.map(enrolment => (
                      <tr key={enrolment.course?.id || courseName(enrolment)}>
                        <td>{courseName(enrolment)}</td>
                        <td>{courseAuthor(enrolment)}</td>
                        <td className="text-nowrap">{formatUnix(enrolment.created)}</td>
                        <td className="text-nowrap">{formatUnix(enrolment.expires, "Never")}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        {hasFetched ? "No LearnWorlds courses found" : "—"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <Pagination
                totalRows={total}
                pageChangeHandler={setPage}
                rowsPerPage={perPage}
                initialPage={page} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default LearnWorldsCourses
